/* eslint-disable no-unused-vars */
import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Home, Eye, EyeOff, RefreshCw, LogOut, Edit2, Save, X, Upload, Download, Volume2, Sparkles, Hash, Trophy, Crown, Star, FileDown, FileUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { API, BACKEND_URL, resolveUrl, useAdmin } from "../adminShared";
import { PlacementEditor } from "../PlacementEditor";
import { VotingColumnEditor } from "../VotingColumnEditor";

const Arrow12MiniPreview = ({ color, thickness, headSize, text }) => (
  <div className="rounded-lg border border-zinc-700 bg-zinc-950 px-6 py-4 flex items-center justify-center" data-testid="preview-overlay-12-mini">
    <div className="flex flex-col items-start" style={{ minWidth: '55%' }}>
      <div className="text-white text-xs font-semibold mb-1 whitespace-nowrap">{text}</div>
      <div className="flex items-center self-stretch" style={{ color, marginRight: `${-(headSize + 2) * 0.5}px` }}>
        <div style={{ height: `${Math.max(2, thickness * 0.5)}px`, flexGrow: 1, background: 'currentColor' }} />
        <div style={{ width: 0, height: 0, borderTop: `${headSize * 0.228}px solid transparent`, borderBottom: `${headSize * 0.228}px solid transparent`, borderLeft: `${headSize * 0.5}px solid currentColor` }} />
      </div>
      <div className="text-white text-[10px] font-semibold mt-1 self-end">12 p.</div>
    </div>
  </div>
);

const WinnerMiniPreview = ({ icon, iconUrl, accentColor, title }) => {
  const Icon = icon === 'crown' ? Crown : icon === 'star' ? Star : Trophy;
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-950 px-6 py-4 flex flex-col items-center gap-1" data-testid="preview-winner-mini">
      <div className="text-white text-xs font-medium tracking-[0.15em] text-center">{title || "¡ENHORABUENA!"}</div>
      <div className="text-white text-sm font-bold tracking-[0.35em] uppercase">PAÍS</div>
      {icon === 'custom' && iconUrl ? (
        <img src={iconUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${iconUrl}` : iconUrl} alt="Icono" className="object-contain" style={{ height: 34, filter: `drop-shadow(0 0 8px ${accentColor || 'rgba(255,255,255,0.7)'})` }} />
      ) : (
        <Icon style={{ color: accentColor || '#FFFFFF', width: 30, height: 30, filter: `drop-shadow(0 0 8px ${accentColor || 'rgba(255,255,255,0.7)'})` }} />
      )}
    </div>
  );
};

export const TextsOverlaysSection = () => {
  const {
    countries, setCountries, votes, setVotes, events, setEvents,
    activeEvent, setActiveEvent, showCreateEvent, setShowCreateEvent, newEventName, setNewEventName,
    copyFromEventId, setCopyFromEventId, creatingEvent, setCreatingEvent, showRenameEvent, setShowRenameEvent,
    renameEventName, setRenameEventName, importFile, setImportFile, showImportConfirm, setShowImportConfirm,
    importing, setImporting, newCountry, setNewCountry, editingCountry, setEditingCountry,
    backgroundVideo, setBackgroundVideo, bgVideoBlur, setBgVideoBlur, bgVideoBrightness, setBgVideoBrightness, bgVideoOpacity, setBgVideoOpacity, fontTitle, setFontTitle, fontSubtitle, setFontSubtitle,
    fontCountry, setFontCountry, fontPoints, setFontPoints, fontVoting, setFontVoting,
    customFontTitleUrl, setCustomFontTitleUrl, customFontTitleName, setCustomFontTitleName, customFontSubtitleUrl, setCustomFontSubtitleUrl,
    customFontSubtitleName, setCustomFontSubtitleName, customFontCountryUrl, setCustomFontCountryUrl, customFontCountryName, setCustomFontCountryName,
    customFontPointsUrl, setCustomFontPointsUrl, customFontPointsName, setCustomFontPointsName, customFontVotingUrl, setCustomFontVotingUrl,
    customFontVotingName, setCustomFontVotingName, fontSizeTitle, setFontSizeTitle, fontSizeSubtitle, setFontSizeSubtitle,
    fontSizeCountry, setFontSizeCountry, fontSizePoints, setFontSizePoints, fontSizeVoting, setFontSizeVoting,
    primaryColor, setPrimaryColor, secondaryColor, setSecondaryColor, tertiaryColor, setTertiaryColor,
    rowBgEven, setRowBgEven, rowBgOdd, setRowBgOdd, rowBgEvenTranslucent, setRowBgEvenTranslucent, rowBgOddTranslucent, setRowBgOddTranslucent, rowBadgeTranslucent, setRowBadgeTranslucent, logoUrl, setLogoUrl,
    logoPosition, setLogoPosition, logoSize, setLogoSize, logoZIndex, setLogoZIndex,
    logoOpacity, setLogoOpacity, extraImage1Url, setExtraImage1Url, extraImage1Position, setExtraImage1Position,
    extraImage1Size, setExtraImage1Size, extraImage1ZIndex, setExtraImage1ZIndex, extraImage1Opacity, setExtraImage1Opacity,
    extraImage2Url, setExtraImage2Url, extraImage2Position, setExtraImage2Position, extraImage2Size, setExtraImage2Size,
    extraImage2ZIndex, setExtraImage2ZIndex, extraImage2Opacity, setExtraImage2Opacity, extraImage3Url, setExtraImage3Url,
    extraImage3Position, setExtraImage3Position, extraImage3Size, setExtraImage3Size, extraImage3ZIndex, setExtraImage3ZIndex,
    extraImage3Opacity, setExtraImage3Opacity, defaultArtistPhoto, setDefaultArtistPhoto, artistPhotoSizeMode, setArtistPhotoSizeMode,
    artistPhotoSizePx, setArtistPhotoSizePx, textTitle, setTextTitle, textSubtitle, setTextSubtitle,
    textVotingNow, setTextVotingNow, textWaiting, setTextWaiting, sound12PointsUrl, setSound12PointsUrl,
    confettiEnabled, setConfettiEnabled, confettiColors, setConfettiColors, top1Color, setTop1Color,
    top2Color, setTop2Color, top3Color, setTop3Color, lastPlaceColor, setLastPlaceColor,
    top1Translucent, setTop1Translucent, top2Translucent, setTop2Translucent, top3Translucent, setTop3Translucent,
    lastPlaceTranslucent, setLastPlaceTranslucent, positionBadgeTranslucent, setPositionBadgeTranslucent, votingColumnCenter, setVotingColumnCenter, leftColSpan, setLeftColSpan,
    rightColSpan, setRightColSpan, votingColSpan, setVotingColSpan, columnGap, setColumnGap,
    subtitleCounterEnabled, setSubtitleCounterEnabled, subtitlePhaseOrder, setSubtitlePhaseOrder, subtitleJuryLabel, setSubtitleJuryLabel,
    subtitleTelevoteLabel, setSubtitleTelevoteLabel, mystery12Text, setMystery12Text, mysteryHiddenPoints, setMysteryHiddenPoints, voteDisplayColumns, setVoteDisplayColumns,
    top6TitleText, setTop6TitleText,
    top6StyleCustom, setTop6StyleCustom, top6FontTitle, setTop6FontTitle, top6FontCountry, setTop6FontCountry,
    overlay12CustomFontUrl, setOverlay12CustomFontUrl, winnerCustomFontUrl, setWinnerCustomFontUrl,
    showVoterBorder, setShowVoterBorder, showJuryBadge, setShowJuryBadge, fontVotingSecondary, setFontVotingSecondary,
    fontTitleItalic, setFontTitleItalic, fontSubtitleItalic, setFontSubtitleItalic, fontCountryItalic, setFontCountryItalic,
    fontPointsItalic, setFontPointsItalic, fontVotingItalic, setFontVotingItalic, fontVotingSecondaryItalic, setFontVotingSecondaryItalic,
    customFontVotingSecondaryUrl, setCustomFontVotingSecondaryUrl, customFontVotingSecondaryName, setCustomFontVotingSecondaryName, overlay12Enabled, setOverlay12Enabled,
    overlay12DurationMode, setOverlay12DurationMode, overlay12DurationSeconds, setOverlay12DurationSeconds, overlay12BgStyle, setOverlay12BgStyle,
    overlay12BgColor, setOverlay12BgColor, overlay12BgOpacity, setOverlay12BgOpacity, overlay12TextReceives, setOverlay12TextReceives,
    overlay12UseAdminColors, setOverlay12UseAdminColors, overlay12ArrowColor, setOverlay12ArrowColor, overlay12ArrowThickness, setOverlay12ArrowThickness, overlay12ArrowHeadSize, setOverlay12ArrowHeadSize, overlay12ShowConfetti, setOverlay12ShowConfetti, overlay12Visible, setOverlay12Visible, previewGiverId, setPreviewGiverId,
    previewRecipientId, setPreviewRecipientId, winnerOverlayEnabled, setWinnerOverlayEnabled, winnerOverlayDurationMode, setWinnerOverlayDurationMode,
    winnerOverlayDurationSeconds, setWinnerOverlayDurationSeconds, winnerOverlayBgStyle, setWinnerOverlayBgStyle, winnerOverlayBgColor, setWinnerOverlayBgColor,
    winnerOverlayBgOpacity, setWinnerOverlayBgOpacity, winnerOverlayTitleText, setWinnerOverlayTitleText, winnerOverlaySubtitleText, setWinnerOverlaySubtitleText,
    winnerOverlayUseAdminColors, setWinnerOverlayUseAdminColors, winnerOverlayAccentColor, setWinnerOverlayAccentColor, winnerOverlayIcon, setWinnerOverlayIcon, winnerOverlayIconUrl, setWinnerOverlayIconUrl, winnerOverlayShowConfetti, setWinnerOverlayShowConfetti, winnerOverlayShowPoints, setWinnerOverlayShowPoints,
    winnerOverlayVisible, setWinnerOverlayVisible, overlay12StyleCustom, setOverlay12StyleCustom, overlay12FontTitle, setOverlay12FontTitle,
    overlay12FontCountry, setOverlay12FontCountry, overlay12FontSecondary, setOverlay12FontSecondary, overlay12SizeScale, setOverlay12SizeScale,
    winnerOverlayStyleCustom, setWinnerOverlayStyleCustom, winnerOverlayFontTitle, setWinnerOverlayFontTitle, winnerOverlayFontCountry, setWinnerOverlayFontCountry,
    winnerOverlayFontSecondary, setWinnerOverlayFontSecondary, winnerOverlaySizeScale, setWinnerOverlaySizeScale, loading, setLoading,
    exportingPdf, setExportingPdf, winnerPreviewId, setWinnerPreviewId, adminInitRef, adminPollRef,
    navigate, fetchCurrentVoterStatus, handleHideOverlay12, handlePreviewOverlay12, handleShowWinner, handleHideWinner,
    getFontFormat, fetchBackground, handleSetBackground, handleExportPdf, handleFileUpload, fetchCountries,
    fetchVotes, downloadFromApi, handleExportVotesPdf, handleExportVotesExcel, fetchEvents, reloadEventData,
    handleSelectEvent, handleCreateEvent, handleRenameEvent, handleExportEvent, handleImportEvent, handleAddCountry,
    handleDeleteCountry, handleSetCurrentVoter, handleResetVotes, handleLogout, handleApplyVotes, handleReveal12Points,
    handleEditCountry, handleUpdateCountry, handleToggleBadge, handleToggleTranslucent, votingCountries, juryMembers,
    votedCountries,
  } = useAdmin();
  // Convert a hex/rgba color string into a #rrggbb value usable by <input type="color">.
  const toPickerHex = (c) => {
    if (!c || typeof c !== 'string') return "#000000";
    if (c.startsWith('#')) return c.slice(0, 7).padEnd(7, '0');
    const m = c.match(/rgba?\(([^)]+)\)/i);
    if (m) {
      const parts = m[1].split(',').map((s) => parseInt(s.trim(), 10));
      const h = (n) => Math.max(0, Math.min(255, isNaN(n) ? 0 : n)).toString(16).padStart(2, '0');
      return `#${h(parts[0])}${h(parts[1])}${h(parts[2])}`;
    }
    return "#000000";
  };
  return (
    <>
                {/* Textos Personalizables */}
                <div className="p-4 bg-zinc-800/30 rounded-lg border border-zinc-700">
                  <Label className="text-zinc-300 mb-3 block font-semibold text-lg">Textos Personalizables</Label>
                  <p className="text-zinc-500 text-sm mb-4">Cambia los textos que aparecen en el visualizador</p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="text-title" className="text-zinc-300 mb-2 block">Título Principal</Label>
                      <Input
                        id="text-title"
                        value={textTitle}
                        onChange={(e) => setTextTitle(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="CLASIFICACIÓN EN VIVO"
                      />
                    </div>
                    <div>
                      <Label htmlFor="text-subtitle" className="text-zinc-300 mb-2 block">Subtítulo</Label>
                      <Input
                        id="text-subtitle"
                        value={textSubtitle}
                        onChange={(e) => setTextSubtitle(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Sistema de Votación Eurovisión"
                      />
                    </div>
                    <div>
                      <Label htmlFor="text-voting" className="text-zinc-300 mb-2 block">Texto de Votación</Label>
                      <Input
                        id="text-voting"
                        value={textVotingNow}
                        onChange={(e) => setTextVotingNow(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="VOTANDO AHORA"
                      />
                    </div>
                    <div>
                      <Label htmlFor="text-waiting" className="text-zinc-300 mb-2 block">Texto de Espera</Label>
                      <Input
                        id="text-waiting"
                        value={textWaiting}
                        onChange={(e) => setTextWaiting(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Esperando próxima votación..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="mystery-12-text" className="text-zinc-300 mb-2 block">Texto Misterio 12 Puntos</Label>
                      <Input
                        id="mystery-12-text"
                        data-testid="input-mystery-12-text"
                        value={mystery12Text}
                        onChange={(e) => setMystery12Text(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="?????????"
                      />
                      <p className="text-zinc-500 text-xs mt-1">Se muestra en lugar de los 12 puntos hasta que pulses &quot;Mostrar 12 pts&quot;</p>
                    </div>
                    <div>
                      <Label htmlFor="mystery-hidden-points" className="text-zinc-300 mb-2 block">Puntos ocultos en el misterio</Label>
                      <select
                        id="mystery-hidden-points"
                        data-testid="select-mystery-hidden-points"
                        value={mysteryHiddenPoints}
                        onChange={(e) => setMysteryHiddenPoints(e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
                      >
                        <option value="12">Solo 12 puntos</option>
                        <option value="12_10">12 y 10 puntos</option>
                        <option value="12_10_8">12, 10 y 8 puntos</option>
                      </select>
                      <p className="text-zinc-500 text-xs mt-1">Estos puntos se ocultan con el texto misterio hasta que pulses &quot;12 Pts&quot;</p>
                    </div>
                    <div>
                      <Label htmlFor="top6-title-text" className="text-zinc-300 mb-2 block">Título del Overlay Top 6</Label>
                      <Input
                        id="top6-title-text"
                        data-testid="input-top6-title-text"
                        value={top6TitleText}
                        onChange={(e) => setTop6TitleText(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="Current top 6:"
                      />
                      <p className="text-zinc-500 text-xs mt-1">Se muestra como título al revelar el Top 6 desde Control Pantalla</p>
                      <div className="mt-3 pt-3 border-t border-zinc-700 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-zinc-200 text-sm font-semibold">Fuentes personalizadas del Top 6</Label>
                          <button
                            type="button"
                            data-testid="toggle-top6-style-custom"
                            onClick={() => setTop6StyleCustom(!top6StyleCustom)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${top6StyleCustom ? 'bg-emerald-600' : 'bg-zinc-700'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${top6StyleCustom ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        <p className="text-zinc-500 text-xs">{top6StyleCustom ? "Usando fuentes personalizadas de este overlay" : "Heredando fuentes globales del Admin"}</p>
                        {top6StyleCustom && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-zinc-400 text-xs mb-1 block">Fuente Título</Label>
                              <select value={top6FontTitle} onChange={(e) => setTop6FontTitle(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2" data-testid="select-top6-font-title">
                                <option value="Unbounded">Unbounded</option>
                                <option value="Inter">Inter</option>
                                <option value="Roboto">Roboto</option>
                                <option value="Poppins">Poppins</option>
                                <option value="Montserrat">Montserrat</option>
                                <option value="Oswald">Oswald</option>
                                <option value="Custom">Custom (subida en Admin)</option>
                              </select>
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs mb-1 block">Fuente Posiciones/Países</Label>
                              <select value={top6FontCountry} onChange={(e) => setTop6FontCountry(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2" data-testid="select-top6-font-country">
                                <option value="Unbounded">Unbounded</option>
                                <option value="Inter">Inter</option>
                                <option value="Roboto">Roboto</option>
                                <option value="Poppins">Poppins</option>
                                <option value="Montserrat">Montserrat</option>
                                <option value="Oswald">Oswald</option>
                                <option value="Custom">Custom</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 12-points Overlay Config */}
                    <div className="md:col-span-2 p-4 bg-zinc-800/30 rounded-lg border border-zinc-700 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-zinc-200 font-semibold text-base">Overlay de 12 Puntos</Label>
                          <p className="text-zinc-500 text-xs mt-1">Pantalla completa al revelar los 12 puntos: bandera de quien vota → bandera de quien recibe</p>
                        </div>
                        <button
                          type="button"
                          data-testid="toggle-overlay-12-enabled"
                          onClick={() => setOverlay12Enabled(!overlay12Enabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${overlay12Enabled ? 'bg-pink-600' : 'bg-zinc-700'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${overlay12Enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>

                      {overlay12Enabled && (
                        <>
                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Duración</Label>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                data-testid="btn-overlay-12-auto"
                                onClick={() => setOverlay12DurationMode("auto")}
                                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${overlay12DurationMode === "auto" ? "bg-pink-600 border-pink-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}
                              >
                                Automático
                              </button>
                              <button
                                type="button"
                                data-testid="btn-overlay-12-manual"
                                onClick={() => setOverlay12DurationMode("manual")}
                                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${overlay12DurationMode === "manual" ? "bg-pink-600 border-pink-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}
                              >
                                Manual
                              </button>
                            </div>
                            {overlay12DurationMode === "auto" && (
                              <div className="mt-2">
                                <Label className="text-zinc-400 text-xs mb-1 block">Segundos: {overlay12DurationSeconds}</Label>
                                <Input
                                  type="number"
                                  min={1}
                                  max={60}
                                  value={overlay12DurationSeconds}
                                  onChange={(e) => setOverlay12DurationSeconds(parseInt(e.target.value) || 8)}
                                  className="bg-zinc-800 border-zinc-700 text-white"
                                  data-testid="input-overlay-12-duration"
                                />
                              </div>
                            )}
                          </div>

                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Estilo de fondo</Label>
                            <div className="flex gap-2">
                              <button type="button" data-testid="btn-overlay-bg-gradient" onClick={() => setOverlay12BgStyle("gradient")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${overlay12BgStyle === "gradient" ? "bg-pink-600 border-pink-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}>Gradiente</button>
                              <button type="button" data-testid="btn-overlay-bg-transparent" onClick={() => setOverlay12BgStyle("transparent")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${overlay12BgStyle === "transparent" ? "bg-pink-600 border-pink-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}>Transparente</button>
                              <button type="button" data-testid="btn-overlay-bg-solid" onClick={() => setOverlay12BgStyle("solid")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${overlay12BgStyle === "solid" ? "bg-pink-600 border-pink-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}>Sólido</button>
                            </div>
                          </div>

                          {overlay12BgStyle === "solid" && (
                            <div>
                              <Label className="text-zinc-300 mb-2 block text-sm">Color de fondo</Label>
                              <div className="flex gap-2">
                                <input type="color" value={overlay12BgColor} onChange={(e) => setOverlay12BgColor(e.target.value)} className="w-16 h-10 rounded cursor-pointer border border-zinc-700" />
                                <Input value={overlay12BgColor} onChange={(e) => setOverlay12BgColor(e.target.value)} className="flex-1 bg-zinc-800 border-zinc-700 text-white" />
                              </div>
                            </div>
                          )}

                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Opacidad de fondo: {overlay12BgOpacity}%</Label>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              value={overlay12BgOpacity}
                              onChange={(e) => setOverlay12BgOpacity(parseInt(e.target.value))}
                              className="w-full"
                              data-testid="input-overlay-12-opacity"
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <Label className="text-zinc-300 text-sm">Usar colores del Admin (texto/acentos)</Label>
                            <button
                              type="button"
                              data-testid="toggle-overlay-12-admin-colors"
                              onClick={() => setOverlay12UseAdminColors(!overlay12UseAdminColors)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${overlay12UseAdminColors ? 'bg-pink-600' : 'bg-zinc-700'}`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${overlay12UseAdminColors ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          </div>

                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Color de la flecha</Label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={overlay12ArrowColor || "#2EE58A"}
                                onChange={(e) => setOverlay12ArrowColor(e.target.value)}
                                className="w-16 h-10 rounded cursor-pointer border border-zinc-700"
                                data-testid="input-overlay-12-arrow-color"
                              />
                              <Input
                                value={overlay12ArrowColor}
                                onChange={(e) => setOverlay12ArrowColor(e.target.value)}
                                placeholder="Automático"
                                className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                                data-testid="input-overlay-12-arrow-color-text"
                              />
                              <Button
                                type="button"
                                onClick={() => setOverlay12ArrowColor("")}
                                className="bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                                data-testid="btn-overlay-12-arrow-color-auto"
                              >
                                Auto
                              </Button>
                            </div>
                            <p className="text-zinc-500 text-xs mt-1">Vacío = automático (color primario de la edición)</p>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-zinc-300 mb-2 block text-sm">Grosor de flecha: {overlay12ArrowThickness}px</Label>
                              <input
                                type="range"
                                min={4}
                                max={40}
                                value={overlay12ArrowThickness}
                                onChange={(e) => setOverlay12ArrowThickness(parseInt(e.target.value))}
                                className="w-full"
                                data-testid="input-overlay-12-arrow-thickness"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-300 mb-2 block text-sm">Tamaño de punta: {overlay12ArrowHeadSize}px</Label>
                              <input
                                type="range"
                                min={20}
                                max={100}
                                value={overlay12ArrowHeadSize}
                                onChange={(e) => setOverlay12ArrowHeadSize(parseInt(e.target.value))}
                                className="w-full"
                                data-testid="input-overlay-12-arrow-head"
                              />
                            </div>
                          </div>

                          <div>
                            <Label className="text-zinc-400 mb-2 block text-xs">Vista previa de la flecha</Label>
                            <Arrow12MiniPreview
                              color={overlay12ArrowColor || (overlay12UseAdminColors ? primaryColor : "#2EE58A")}
                              thickness={overlay12ArrowThickness}
                              headSize={overlay12ArrowHeadSize}
                              text={overlay12TextReceives}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="text-zinc-300 text-sm">Mostrar confeti</Label>
                              <p className="text-zinc-500 text-xs">Un splash al revelar quién recibe los 12 puntos</p>
                            </div>
                            <button
                              type="button"
                              data-testid="toggle-overlay-12-confetti"
                              onClick={() => setOverlay12ShowConfetti(!overlay12ShowConfetti)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${overlay12ShowConfetti ? 'bg-pink-600' : 'bg-zinc-700'}`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${overlay12ShowConfetti ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          </div>

                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Texto central</Label>
                            <Input
                              value={overlay12TextReceives}
                              onChange={(e) => setOverlay12TextReceives(e.target.value)}
                              className="bg-zinc-800 border-zinc-700 text-white"
                              placeholder="OTORGA 12 PUNTOS A"
                              data-testid="input-overlay-12-text"
                            />
                          </div>

                          {overlay12Visible && (
                            <Button
                              type="button"
                              onClick={handleHideOverlay12}
                              className="w-full bg-red-600 hover:bg-red-500 text-white"
                              data-testid="btn-hide-overlay-12"
                            >
                              Ocultar Overlay Ahora
                            </Button>
                          )}

                          <div className="mt-4 pt-4 border-t border-zinc-700 space-y-2">
                            <Label className="text-zinc-200 text-sm font-semibold flex items-center gap-2">
                              <Sparkles className="w-4 h-4" /> Modo Ensayo
                            </Label>
                            <p className="text-zinc-500 text-xs">Previsualiza el overlay sin necesidad de votos reales. Abre el Visualizer en otra pestaña para ver el resultado.</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-zinc-400 text-xs mb-1 block">Otorga (izquierda)</Label>
                                <select
                                  value={previewGiverId}
                                  onChange={(e) => setPreviewGiverId(e.target.value)}
                                  className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2"
                                  data-testid="select-preview-giver"
                                >
                                  <option value="">— Selecciona —</option>
                                  {countries.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}{c.is_jury ? ' (Jurado)' : ''}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <Label className="text-zinc-400 text-xs mb-1 block">Recibe (derecha)</Label>
                                <select
                                  value={previewRecipientId}
                                  onChange={(e) => setPreviewRecipientId(e.target.value)}
                                  className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2"
                                  data-testid="select-preview-recipient"
                                >
                                  <option value="">— Selecciona —</option>
                                  {countries.filter(c => !c.is_jury).map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <Button
                              type="button"
                              onClick={handlePreviewOverlay12}
                              className="w-full bg-yellow-600 hover:bg-yellow-500 text-white"
                              data-testid="btn-preview-overlay-12"
                            >
                              <Sparkles className="w-4 h-4 mr-2" />
                              Previsualizar Overlay
                            </Button>
                          </div>

                          {/* Estilo Avanzado para Overlay 12 */}
                          <div className="mt-4 pt-4 border-t border-zinc-700 space-y-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-zinc-200 text-sm font-semibold">Estilo Avanzado (fuentes + tamaño)</Label>
                              <button
                                type="button"
                                data-testid="toggle-overlay-12-style-custom"
                                onClick={() => setOverlay12StyleCustom(!overlay12StyleCustom)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${overlay12StyleCustom ? 'bg-pink-600' : 'bg-zinc-700'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${overlay12StyleCustom ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                            </div>
                            <p className="text-zinc-500 text-xs">{overlay12StyleCustom ? "Usando configuración personalizada de este overlay" : "Heredando fuentes globales del Admin"}</p>
                            {overlay12StyleCustom && (
                              <div className="space-y-2">
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <Label className="text-zinc-400 text-xs mb-1 block">Fuente Banderas/Nombres</Label>
                                    <select value={overlay12FontTitle} onChange={(e) => setOverlay12FontTitle(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2" data-testid="select-overlay-12-font-title">
                                      <option value="Unbounded">Unbounded</option>
                                      <option value="Inter">Inter</option>
                                      <option value="Roboto">Roboto</option>
                                      <option value="Poppins">Poppins</option>
                                      <option value="Montserrat">Montserrat</option>
                                      <option value="Oswald">Oswald</option>
                                      <option value="Custom">Custom (subida en Admin)</option>
                                      <option value="OverlayCustom">Fuente propia (este overlay)</option>
                                    </select>
                                  </div>
                                  <div>
                                    <Label className="text-zinc-400 text-xs mb-1 block">Fuente Nombres País</Label>
                                    <select value={overlay12FontCountry} onChange={(e) => setOverlay12FontCountry(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2" data-testid="select-overlay-12-font-country">
                                      <option value="Unbounded">Unbounded</option>
                                      <option value="Inter">Inter</option>
                                      <option value="Roboto">Roboto</option>
                                      <option value="Poppins">Poppins</option>
                                      <option value="Montserrat">Montserrat</option>
                                      <option value="Oswald">Oswald</option>
                                      <option value="Custom">Custom</option>
                                      <option value="OverlayCustom">Fuente propia (este overlay)</option>
                                    </select>
                                  </div>
                                  <div>
                                    <Label className="text-zinc-400 text-xs mb-1 block">Fuente Texto/12</Label>
                                    <select value={overlay12FontSecondary} onChange={(e) => setOverlay12FontSecondary(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2" data-testid="select-overlay-12-font-secondary">
                                      <option value="Unbounded">Unbounded</option>
                                      <option value="Inter">Inter</option>
                                      <option value="Roboto">Roboto</option>
                                      <option value="Poppins">Poppins</option>
                                      <option value="Montserrat">Montserrat</option>
                                      <option value="Oswald">Oswald</option>
                                      <option value="Custom">Custom</option>
                                      <option value="OverlayCustom">Fuente propia (este overlay)</option>
                                    </select>
                                  </div>
                                </div>
                                                                <div>
                                  <Label className="text-zinc-400 text-xs mb-1 block">Fuente propia de este overlay (.ttf, .otf, .woff, .woff2)</Label>
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="file"
                                      id="overlay12-font-file"
                                      accept=".ttf,.otf,.woff,.woff2"
                                      className="hidden"
                                      onChange={async (e) => {
                                        const f = e.target.files[0];
                                        if (f) {
                                          const url = await handleFileUpload(f, 'temp');
                                          if (url) {
                                            setOverlay12CustomFontUrl(url);
                                            setOverlay12FontTitle('OverlayCustom');
                                            setOverlay12FontCountry('OverlayCustom');
                                            setOverlay12FontSecondary('OverlayCustom');
                                          }
                                        }
                                      }}
                                    />
                                    <Button type="button" onClick={() => document.getElementById('overlay12-font-file').click()} variant="outline" className="flex-1 bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 text-xs" data-testid="btn-upload-overlay12-font">
                                      <Upload className="w-3 h-3 mr-2" />
                                      Subir fuente propia
                                    </Button>
                                    {overlay12CustomFontUrl && (
                                      <Button type="button" onClick={() => setOverlay12CustomFontUrl("")} variant="outline" size="icon" className="bg-red-900/30 border-red-800 hover:bg-red-900/50 text-red-400" title="Quitar fuente propia">
                                        <X className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                  {overlay12CustomFontUrl && <p className="text-emerald-400 text-xs mt-1">Fuente propia subida — se aplica con la opcion &quot;Fuente propia (este overlay)&quot;</p>}
                                </div>
                                <div>
                                  <Label className="text-zinc-400 text-xs mb-1 block">Tamaño general</Label>
                                  <div className="flex gap-1">
                                    {['xs','s','m','l','xl'].map(sz => (
                                      <button key={sz} type="button" onClick={() => setOverlay12SizeScale(sz)} className={`flex-1 px-2 py-2 rounded-lg border text-xs font-medium uppercase transition-colors ${overlay12SizeScale === sz ? "bg-pink-600 border-pink-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`} data-testid={`btn-overlay-12-size-${sz}`}>{sz}</button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Winner Overlay Config */}
                    <div className="md:col-span-2 p-4 bg-gradient-to-br from-yellow-900/10 to-pink-900/10 rounded-lg border border-yellow-700/40 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-yellow-200 font-semibold text-base flex items-center gap-2">
                            <Trophy className="w-4 h-4" /> Overlay de Ganador
                          </Label>
                          <p className="text-zinc-500 text-xs mt-1">Pantalla de enhorabuena al país ganador. Se activa manualmente desde Control Pantalla.</p>
                        </div>
                        <button
                          type="button"
                          data-testid="toggle-winner-overlay-enabled"
                          onClick={() => setWinnerOverlayEnabled(!winnerOverlayEnabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${winnerOverlayEnabled ? 'bg-yellow-600' : 'bg-zinc-700'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${winnerOverlayEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>

                      {winnerOverlayEnabled && (
                        <>
                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Duración</Label>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                data-testid="btn-winner-auto"
                                onClick={() => setWinnerOverlayDurationMode("auto")}
                                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${winnerOverlayDurationMode === "auto" ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}
                              >
                                Automático
                              </button>
                              <button
                                type="button"
                                data-testid="btn-winner-manual"
                                onClick={() => setWinnerOverlayDurationMode("manual")}
                                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${winnerOverlayDurationMode === "manual" ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}
                              >
                                Manual
                              </button>
                            </div>
                            {winnerOverlayDurationMode === "auto" && (
                              <div className="mt-2">
                                <Label className="text-zinc-400 text-xs mb-1 block">Segundos: {winnerOverlayDurationSeconds}</Label>
                                <Input
                                  type="number"
                                  min={1}
                                  max={120}
                                  value={winnerOverlayDurationSeconds}
                                  onChange={(e) => setWinnerOverlayDurationSeconds(parseInt(e.target.value) || 15)}
                                  className="bg-zinc-800 border-zinc-700 text-white"
                                  data-testid="input-winner-duration"
                                />
                              </div>
                            )}
                          </div>

                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Estilo de fondo</Label>
                            <div className="flex gap-2">
                              <button type="button" onClick={() => setWinnerOverlayBgStyle("gradient")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${winnerOverlayBgStyle === "gradient" ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}>Gradiente</button>
                              <button type="button" onClick={() => setWinnerOverlayBgStyle("transparent")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${winnerOverlayBgStyle === "transparent" ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}>Transparente</button>
                              <button type="button" onClick={() => setWinnerOverlayBgStyle("solid")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${winnerOverlayBgStyle === "solid" ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}>Sólido</button>
                            </div>
                          </div>

                          {winnerOverlayBgStyle === "solid" && (
                            <div>
                              <Label className="text-zinc-300 mb-2 block text-sm">Color de fondo</Label>
                              <div className="flex gap-2">
                                <input type="color" value={winnerOverlayBgColor} onChange={(e) => setWinnerOverlayBgColor(e.target.value)} className="w-16 h-10 rounded cursor-pointer border border-zinc-700" />
                                <Input value={winnerOverlayBgColor} onChange={(e) => setWinnerOverlayBgColor(e.target.value)} className="flex-1 bg-zinc-800 border-zinc-700 text-white" />
                              </div>
                            </div>
                          )}

                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Opacidad de fondo: {winnerOverlayBgOpacity}%</Label>
                            <input type="range" min={0} max={100} value={winnerOverlayBgOpacity} onChange={(e) => setWinnerOverlayBgOpacity(parseInt(e.target.value))} className="w-full" />
                          </div>

                          <div className="flex items-center justify-between">
                            <Label className="text-zinc-300 text-sm">Usar colores del Admin</Label>
                            <button
                              type="button"
                              onClick={() => setWinnerOverlayUseAdminColors(!winnerOverlayUseAdminColors)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${winnerOverlayUseAdminColors ? 'bg-yellow-600' : 'bg-zinc-700'}`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${winnerOverlayUseAdminColors ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          </div>

                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Color del trofeo (acento)</Label>
                            <div className="flex gap-2">
                              <input
                                type="color"
                                value={winnerOverlayAccentColor || "#FFFFFF"}
                                onChange={(e) => setWinnerOverlayAccentColor(e.target.value)}
                                className="w-16 h-10 rounded cursor-pointer border border-zinc-700"
                                data-testid="input-winner-accent-color"
                              />
                              <Input
                                value={winnerOverlayAccentColor}
                                onChange={(e) => setWinnerOverlayAccentColor(e.target.value)}
                                placeholder="Automático"
                                className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                                data-testid="input-winner-accent-color-text"
                              />
                              <Button
                                type="button"
                                onClick={() => setWinnerOverlayAccentColor("")}
                                className="bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700"
                                data-testid="btn-winner-accent-color-auto"
                              >
                                Auto
                              </Button>
                            </div>
                            <p className="text-zinc-500 text-xs mt-1">Vacío = automático (trofeo blanco)</p>
                          </div>

                          <div>
                            <Label className="text-zinc-300 mb-2 block text-sm">Icono del ganador</Label>
                            <div className="flex gap-2">
                              <button type="button" data-testid="btn-winner-icon-trophy" onClick={() => setWinnerOverlayIcon("trophy")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors flex items-center justify-center gap-2 ${winnerOverlayIcon === "trophy" ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}><Trophy className="w-4 h-4" />Trofeo</button>
                              <button type="button" data-testid="btn-winner-icon-crown" onClick={() => setWinnerOverlayIcon("crown")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors flex items-center justify-center gap-2 ${winnerOverlayIcon === "crown" ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}><Crown className="w-4 h-4" />Corona</button>
                              <button type="button" data-testid="btn-winner-icon-star" onClick={() => setWinnerOverlayIcon("star")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors flex items-center justify-center gap-2 ${winnerOverlayIcon === "star" ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`}><Star className="w-4 h-4" />Estrella</button>
                              <button type="button" data-testid="btn-winner-icon-custom" disabled={!winnerOverlayIconUrl} onClick={() => setWinnerOverlayIcon("custom")} className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors flex items-center justify-center gap-2 ${winnerOverlayIcon === "custom" ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"} ${!winnerOverlayIconUrl ? "opacity-50 cursor-not-allowed" : ""}`}><Upload className="w-4 h-4" />PNG propio</button>
                            </div>
                            <div className="flex gap-2 items-center mt-2">
                              <input
                                type="file"
                                id="winner-icon-file"
                                accept="image/png,image/webp,image/svg+xml,image/gif"
                                className="hidden"
                                onChange={async (e) => {
                                  const f = e.target.files[0];
                                  if (f) {
                                    const url = await handleFileUpload(f, 'temp');
                                    if (url) {
                                      setWinnerOverlayIconUrl(url);
                                      setWinnerOverlayIcon('custom');
                                    }
                                  }
                                }}
                              />
                              <Button type="button" onClick={() => document.getElementById('winner-icon-file').click()} variant="outline" className="flex-1 bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 text-xs" data-testid="btn-upload-winner-icon">
                                <Upload className="w-3 h-3 mr-2" />
                                Subir PNG propio
                              </Button>
                              {winnerOverlayIconUrl && (
                                <Button type="button" onClick={() => { setWinnerOverlayIconUrl(""); if (winnerOverlayIcon === 'custom') setWinnerOverlayIcon('trophy'); }} variant="outline" size="icon" className="bg-red-900/30 border-red-800 hover:bg-red-900/50 text-red-400" title="Quitar PNG propio" data-testid="btn-remove-winner-icon">
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                            {winnerOverlayIconUrl && <p className="text-emerald-400 text-xs mt-1">PNG subido — se usa con la opción &quot;PNG propio&quot;</p>}
                          </div>

                          <div>
                            <Label className="text-zinc-400 mb-2 block text-xs">Vista previa del ganador</Label>
                            <WinnerMiniPreview
                              icon={winnerOverlayIcon}
                              iconUrl={winnerOverlayIconUrl}
                              accentColor={winnerOverlayAccentColor}
                              title={winnerOverlayTitleText}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <Label className="text-zinc-300 text-sm">Mostrar confetti continuo</Label>
                            <button
                              type="button"
                              onClick={() => setWinnerOverlayShowConfetti(!winnerOverlayShowConfetti)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${winnerOverlayShowConfetti ? 'bg-yellow-600' : 'bg-zinc-700'}`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${winnerOverlayShowConfetti ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          </div>

                          <div className="flex items-center justify-between">
                            <Label className="text-zinc-300 text-sm">Mostrar puntos totales</Label>
                            <button
                              type="button"
                              onClick={() => setWinnerOverlayShowPoints(!winnerOverlayShowPoints)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${winnerOverlayShowPoints ? 'bg-yellow-600' : 'bg-zinc-700'}`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${winnerOverlayShowPoints ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-zinc-300 mb-2 block text-sm">Título</Label>
                              <Input
                                value={winnerOverlayTitleText}
                                onChange={(e) => setWinnerOverlayTitleText(e.target.value)}
                                className="bg-zinc-800 border-zinc-700 text-white"
                                placeholder="¡ENHORABUENA!"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-300 mb-2 block text-sm">Subtítulo</Label>
                              <Input
                                value={winnerOverlaySubtitleText}
                                onChange={(e) => setWinnerOverlaySubtitleText(e.target.value)}
                                className="bg-zinc-800 border-zinc-700 text-white"
                                placeholder="GANADOR"
                              />
                            </div>
                          </div>

                          {/* Estilo Avanzado para Winner Overlay */}
                          <div className="mt-4 pt-4 border-t border-zinc-700 space-y-3">
                            <div className="flex items-center justify-between">
                              <Label className="text-zinc-200 text-sm font-semibold">Estilo Avanzado (fuentes + tamaño)</Label>
                              <button
                                type="button"
                                data-testid="toggle-winner-style-custom"
                                onClick={() => setWinnerOverlayStyleCustom(!winnerOverlayStyleCustom)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${winnerOverlayStyleCustom ? 'bg-yellow-600' : 'bg-zinc-700'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${winnerOverlayStyleCustom ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                            </div>
                            <p className="text-zinc-500 text-xs">{winnerOverlayStyleCustom ? "Usando configuración personalizada de este overlay" : "Heredando fuentes globales del Admin"}</p>
                            {winnerOverlayStyleCustom && (
                              <div className="space-y-2">
                                <div className="grid grid-cols-3 gap-2">
                                  <div>
                                    <Label className="text-zinc-400 text-xs mb-1 block">Fuente Título/Bandera</Label>
                                    <select value={winnerOverlayFontTitle} onChange={(e) => setWinnerOverlayFontTitle(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2" data-testid="select-winner-font-title">
                                      <option value="Unbounded">Unbounded</option>
                                      <option value="Inter">Inter</option>
                                      <option value="Roboto">Roboto</option>
                                      <option value="Poppins">Poppins</option>
                                      <option value="Montserrat">Montserrat</option>
                                      <option value="Oswald">Oswald</option>
                                      <option value="Custom">Custom</option>
                                      <option value="OverlayCustom">Fuente propia (este overlay)</option>
                                    </select>
                                  </div>
                                  <div>
                                    <Label className="text-zinc-400 text-xs mb-1 block">Fuente Nombre País</Label>
                                    <select value={winnerOverlayFontCountry} onChange={(e) => setWinnerOverlayFontCountry(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2" data-testid="select-winner-font-country">
                                      <option value="Unbounded">Unbounded</option>
                                      <option value="Inter">Inter</option>
                                      <option value="Roboto">Roboto</option>
                                      <option value="Poppins">Poppins</option>
                                      <option value="Montserrat">Montserrat</option>
                                      <option value="Oswald">Oswald</option>
                                      <option value="Custom">Custom</option>
                                      <option value="OverlayCustom">Fuente propia (este overlay)</option>
                                    </select>
                                  </div>
                                  <div>
                                    <Label className="text-zinc-400 text-xs mb-1 block">Fuente Subtítulo/Puntos</Label>
                                    <select value={winnerOverlayFontSecondary} onChange={(e) => setWinnerOverlayFontSecondary(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm rounded-md px-2 py-2" data-testid="select-winner-font-secondary">
                                      <option value="Unbounded">Unbounded</option>
                                      <option value="Inter">Inter</option>
                                      <option value="Roboto">Roboto</option>
                                      <option value="Poppins">Poppins</option>
                                      <option value="Montserrat">Montserrat</option>
                                      <option value="Oswald">Oswald</option>
                                      <option value="Custom">Custom</option>
                                      <option value="OverlayCustom">Fuente propia (este overlay)</option>
                                    </select>
                                  </div>
                                </div>
                                                                <div>
                                  <Label className="text-zinc-400 text-xs mb-1 block">Fuente propia de este overlay (.ttf, .otf, .woff, .woff2)</Label>
                                  <div className="flex gap-2 items-center">
                                    <input
                                      type="file"
                                      id="winner-font-file"
                                      accept=".ttf,.otf,.woff,.woff2"
                                      className="hidden"
                                      onChange={async (e) => {
                                        const f = e.target.files[0];
                                        if (f) {
                                          const url = await handleFileUpload(f, 'temp');
                                          if (url) {
                                            setWinnerCustomFontUrl(url);
                                            setWinnerOverlayFontTitle('OverlayCustom');
                                            setWinnerOverlayFontCountry('OverlayCustom');
                                            setWinnerOverlayFontSecondary('OverlayCustom');
                                          }
                                        }
                                      }}
                                    />
                                    <Button type="button" onClick={() => document.getElementById('winner-font-file').click()} variant="outline" className="flex-1 bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700 text-xs" data-testid="btn-upload-winner-font">
                                      <Upload className="w-3 h-3 mr-2" />
                                      Subir fuente propia
                                    </Button>
                                    {winnerCustomFontUrl && (
                                      <Button type="button" onClick={() => setWinnerCustomFontUrl("")} variant="outline" size="icon" className="bg-red-900/30 border-red-800 hover:bg-red-900/50 text-red-400" title="Quitar fuente propia">
                                        <X className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                  {winnerCustomFontUrl && <p className="text-emerald-400 text-xs mt-1">Fuente propia subida — se aplica con la opcion &quot;Fuente propia (este overlay)&quot;</p>}
                                </div>
                                <div>
                                  <Label className="text-zinc-400 text-xs mb-1 block">Tamaño general</Label>
                                  <div className="flex gap-1">
                                    {['xs','s','m','l','xl'].map(sz => (
                                      <button key={sz} type="button" onClick={() => setWinnerOverlaySizeScale(sz)} className={`flex-1 px-2 py-2 rounded-lg border text-xs font-medium uppercase transition-colors ${winnerOverlaySizeScale === sz ? "bg-yellow-600 border-yellow-500 text-white" : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"}`} data-testid={`btn-winner-size-${sz}`}>{sz}</button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div>
                      <Label className="text-zinc-300 mb-2 block">Columnas de Puntajes</Label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          data-testid="btn-vote-cols-single"
                          onClick={() => setVoteDisplayColumns("single")}
                          className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            voteDisplayColumns === "single"
                              ? "bg-pink-600 border-pink-500 text-white"
                              : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"
                          }`}
                        >
                          1 Columna
                        </button>
                        <button
                          type="button"
                          data-testid="btn-vote-cols-double"
                          onClick={() => setVoteDisplayColumns("double")}
                          className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            voteDisplayColumns === "double"
                              ? "bg-pink-600 border-pink-500 text-white"
                              : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700"
                          }`}
                        >
                          2 Columnas
                        </button>
                      </div>
                      <p className="text-zinc-500 text-xs mt-1">Disposición de los puntajes recibidos en el Visualizer</p>
                    </div>
                  </div>
                </div>
    </>
  );
};
